import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function FinancialStatements({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Intermediate
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              15 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            📊 Reading Financial Statements
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Master the art of analyzing balance sheets, P&L, and cash flow statements
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Why Bother Reading Financial Statements?</h2>
              <p className="mb-4">
                Would you buy a house without checking its structure? Would you buy a car without knowing its condition? 
                Then why buy stocks without understanding the company's financial health?
              </p>
              <p className="mb-4">
                Financial statements are like a company's report card. They tell you if the business is profitable, growing, 
                loaded with debt, or generating cash. <strong>Every great investor reads these religiously.</strong>
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The 3 Essential Financial Statements</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. Balance Sheet - "What the company owns & owes"</h3>
                  <p className="text-sm mb-2">
                    Shows the company's assets (what it owns), liabilities (what it owes), and equity (shareholders' stake) at a specific point in time.
                  </p>
                  <p className="text-sm italic">Think of it as: A snapshot of the company's financial position on a particular date.</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-l-4 border-purple-500">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Profit & Loss (P&L) - "How much the company earned"</h3>
                  <p className="text-sm mb-2">
                    Shows revenue, expenses, and profit over a period (usually 1 year or 1 quarter). Also called Income Statement.
                  </p>
                  <p className="text-sm italic">Think of it as: A video of the company's earnings performance over time.</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Cash Flow Statement - "Actual cash coming in and going out"</h3>
                  <p className="text-sm mb-2">
                    Shows how much actual cash the company generated from operations, investing, and financing activities.
                  </p>
                  <p className="text-sm italic">Think of it as: Your bank statement - real cash movement, not accounting profits.</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Balance Sheet Deep Dive - TCS Example</h2>
              <p className="mb-4">
                Let's understand using a simplified Balance Sheet of TCS (Tata Consultancy Services):
              </p>
              
              <div className="overflow-x-auto mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="border dark:border-gray-600 rounded">
                    <div className="bg-green-100 dark:bg-green-900/30 p-3 font-bold text-gray-800 dark:text-gray-100">
                      ASSETS (₹ Crores)
                    </div>
                    <div className="p-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Cash & Bank</span>
                        <span className="font-semibold">45,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Receivables (clients owe)</span>
                        <span className="font-semibold">25,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Buildings, Equipment</span>
                        <span className="font-semibold">15,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Other Assets</span>
                        <span className="font-semibold">10,000</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-bold">
                        <span>Total Assets</span>
                        <span>95,000</span>
                      </div>
                    </div>
                  </div>

                  <div className="border dark:border-gray-600 rounded">
                    <div className="bg-red-100 dark:bg-red-900/30 p-3 font-bold text-gray-800 dark:text-gray-100">
                      LIABILITIES & EQUITY (₹ Crores)
                    </div>
                    <div className="p-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Short-term Debt</span>
                        <span className="font-semibold">5,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payables (to suppliers)</span>
                        <span className="font-semibold">8,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Long-term Debt</span>
                        <span className="font-semibold">2,000</span>
                      </div>
                      <div className="flex justify-between text-green-600 dark:text-green-400">
                        <span>Shareholder Equity</span>
                        <span className="font-semibold">80,000</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-bold">
                        <span>Total</span>
                        <span>95,000</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                <p className="text-sm mb-2"><strong>What This Tells Us:</strong></p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  <li>✅ TCS has ₹45,000 Cr in cash - very healthy!</li>
                  <li>✅ Very low debt (₹7,000 Cr) compared to equity (₹80,000 Cr) - financially strong</li>
                  <li>✅ Most value is in shareholder equity, not borrowed money - low risk company</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">P&L Statement - Understanding Profitability</h2>
              <p className="mb-4">
                The P&L shows if the company is making or losing money. Let's see a simplified P&L for Reliance Industries:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Item</th>
                      <th className="p-3 text-right">Amount (₹ Crores)</th>
                      <th className="p-3 text-left">What It Means</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                      <td className="p-3 font-semibold">Revenue (Sales)</td>
                      <td className="p-3 text-right">6,00,000</td>
                      <td className="p-3">Total income from sales</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Cost of Goods Sold</td>
                      <td className="p-3 text-right">-4,50,000</td>
                      <td className="p-3">Direct costs to produce</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-blue-50 dark:bg-blue-900/10">
                      <td className="p-3 font-semibold">Gross Profit</td>
                      <td className="p-3 text-right">1,50,000</td>
                      <td className="p-3">Revenue - Direct costs (25%)</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Operating Expenses</td>
                      <td className="p-3 text-right">-60,000</td>
                      <td className="p-3">Salaries, marketing, admin</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-yellow-50 dark:bg-yellow-900/10">
                      <td className="p-3 font-semibold">Operating Profit (EBIT)</td>
                      <td className="p-3 text-right">90,000</td>
                      <td className="p-3">Core business profit (15%)</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Interest & Tax</td>
                      <td className="p-3 text-right">-25,000</td>
                      <td className="p-3">Loan interest + taxes</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                      <td className="p-3 font-bold">Net Profit (PAT)</td>
                      <td className="p-3 text-right font-bold">65,000</td>
                      <td className="p-3 font-bold">Final profit (10.8%)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                <p className="text-sm mb-2"><strong>Key Insights:</strong></p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  <li><strong>Net Profit Margin = 10.8%</strong> - For every ₹100 of sales, Reliance keeps ₹10.8 as profit</li>
                  <li>Higher margin = more efficient company</li>
                  <li>Compare margins with competitors in the same industry</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Cash Flow - The Most Important Statement</h2>
              <p className="mb-4">
                Warren Buffett says: <em>"Cash is king."</em> A company can show profits on paper but still go bankrupt if it runs out of cash. 
                That's why cash flow is crucial!
              </p>
              
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Operating Cash Flow (OCF)</h4>
                  <p className="text-sm mb-1">Cash generated from core business operations.</p>
                  <p className="text-sm font-mono">Example: TCS gets ₹40,000 Cr from its IT services</p>
                  <p className="text-sm mt-2">👉 <strong>Look for:</strong> Positive and growing OCF</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Investing Cash Flow (ICF)</h4>
                  <p className="text-sm mb-1">Cash used to buy assets, equipment, or investments.</p>
                  <p className="text-sm font-mono">Example: Reliance spends ₹50,000 Cr on new refineries</p>
                  <p className="text-sm mt-2">👉 <strong>Usually negative:</strong> Companies invest for future growth</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Financing Cash Flow (FCF)</h4>
                  <p className="text-sm mb-1">Cash from borrowing, repaying debt, or paying dividends.</p>
                  <p className="text-sm font-mono">Example: Company raises ₹20,000 Cr loan or pays ₹5,000 Cr dividend</p>
                  <p className="text-sm mt-2">👉 <strong>Can be +ve or -ve:</strong> Depends on capital needs</p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded mt-4 border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>🚨 Red Flag:</strong> If a company shows profits in P&L but negative operating cash flow repeatedly, 
                  be very cautious! It might be manipulating profits.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Where to Find These Statements?</h2>
              
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">✅ Company's Website - Investor Relations Section</p>
                  <p className="text-sm">Search "[Company Name] investor relations" on Google</p>
                </div>
                
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">✅ BSE/NSE Websites</p>
                  <p className="text-sm">www.bseindia.com or www.nseindia.com - search for the company and download annual reports</p>
                </div>
                
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">✅ Screener.in (Best for Indian Investors!)</p>
                  <p className="text-sm">www.screener.in - Shows 10 years of financial data in easy-to-read format. Highly recommended!</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Balance Sheet:</strong> Shows what company owns vs owes. Look for low debt and high equity.</li>
                <li><strong>P&L Statement:</strong> Shows profitability. Look for consistent, growing net profit margins.</li>
                <li><strong>Cash Flow:</strong> Shows actual cash movement. Positive operating cash flow is crucial!</li>
                <li><strong>Read all 3 together:</strong> They tell the complete story. Don't rely on just one.</li>
                <li><strong>Compare over time:</strong> Look at trends over 5-10 years, not just one year.</li>
                <li><strong>Use Screener.in:</strong> Makes reading financials super easy for Indian stocks.</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/compounding')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Compounding
          </Button>
          <Button 
            onClick={() => navigate('/learn/key-metrics')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Key Metrics
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
